import React from 'react';

import { formatDeductibleOrOop } from 'Containers/ResultPage/CompareModal/DeductiblesLimits/_shared';
import { PlanCardRow } from 'Containers/ViewPlanPage/_shared/styled';
import { SplitCostRow, SplitCostWrapper } from 'Containers/ViewPlanPage/PlanEssentialsCard/styled';
import { ViewPlanPageCard } from 'Containers/ViewPlanPage/ViewPlanPageCard';
import { BreakdownTag } from 'Containers/ViewPlanPage/ViewPlanPageCard/BreakdownTag';
import { useTextContext } from 'Contexts/textContext';
import { FormattedTooltipProps, Numbers } from 'DesignLibrary/atoms';
import { TooltipPlanDetails } from 'DesignLibrary/molecules';
import { IconBlockProps } from 'DesignLibrary/molecules/IconBlock';
import Plan from 'Models/plan';
import Text from 'Shared/Text';

export interface CombinedNetworkEssentialsProps {
  plan: Plan;
}

export const CombinedNetworkEssentials = (props: CombinedNetworkEssentialsProps) => {
  const { plan } = props;
  const {
    combinedNetworkMedDeductible,
    combinedNetworkDrugDeductible,
    combinedNetworkDeductible,
    combinedNetworkMedOopMax,
    combinedNetworkDrugOopMax,
    combinedNetworkOopMax,
  } = plan;

  const { retrieveContentfulData } = useTextContext();

  const hasComprehensiveDeductible = plan.getIsCombinedNetworkDeductibleComprehensive();
  const hasComprehensiveOopMax = plan.getIsCombinedNetworkOopLimitComprehensive();

  const deductibleTooltip = retrieveContentfulData<FormattedTooltipProps>(
    'tool_tips.combined_network_deductible',
  );
  const oopMaxTooltip = retrieveContentfulData<FormattedTooltipProps>(
    'tool_tips.combined_network_out_of_pocket_limit',
  );

  const title = retrieveContentfulData<string>(
    'health_details_section.plan_details.plan_essentials_card_title',
  );
  const icon: IconBlockProps = {
    icon: 'Sparkle',
    color: 'blue',
  };

  return (
    <ViewPlanPageCard title={title} icon={icon}>
      <PlanCardRow data-testid="combined-network-deductible">
        <TooltipPlanDetails
          tooltip={deductibleTooltip}
          keyHint={`plan-${plan.planId}-combined-network-deductible`}
        >
          {deductibleTooltip.underline as string}
        </TooltipPlanDetails>
        {hasComprehensiveDeductible ? (
          <Numbers className="combined-network-deductible">
            {formatDeductibleOrOop(combinedNetworkDeductible)}
          </Numbers>
        ) : (
          <SplitCostWrapper>
            <SplitCostRow>
              <BreakdownTag>
                <Text field="health_section.comprehensive_plan_terms.medical" />
              </BreakdownTag>
              <Numbers className="combined-network-med-oop-max">
                {formatDeductibleOrOop(combinedNetworkMedDeductible)}
              </Numbers>
            </SplitCostRow>
            <SplitCostRow>
              <BreakdownTag>
                <Text field="health_section.comprehensive_plan_terms.drug" />
              </BreakdownTag>
              <Numbers className="combined-network-drug-oop-max">
                {formatDeductibleOrOop(combinedNetworkDrugDeductible)}
              </Numbers>
            </SplitCostRow>
          </SplitCostWrapper>
        )}
      </PlanCardRow>
      <PlanCardRow data-testid="combined-network-oop-max">
        <TooltipPlanDetails tooltip={oopMaxTooltip} keyHint={`plan-${plan.planId}-combined-network-oop-max`}>
          {oopMaxTooltip.underline as string}
        </TooltipPlanDetails>
        {hasComprehensiveOopMax ? (
          <Numbers className="combined-network-oop-max">
            {formatDeductibleOrOop(combinedNetworkOopMax)}
          </Numbers>
        ) : (
          <SplitCostWrapper>
            <SplitCostRow>
              <BreakdownTag>
                <Text field="health_section.comprehensive_plan_terms.medical" />
              </BreakdownTag>
              <Numbers className="combined-network-med-oop-max">
                {formatDeductibleOrOop(combinedNetworkMedOopMax)}
              </Numbers>
            </SplitCostRow>

            <SplitCostRow>
              <BreakdownTag>
                <Text field="health_section.comprehensive_plan_terms.drug" />
              </BreakdownTag>
              <Numbers className="combined-network-drug-oop-max">
                {formatDeductibleOrOop(combinedNetworkDrugOopMax)}
              </Numbers>
            </SplitCostRow>
          </SplitCostWrapper>
        )}
      </PlanCardRow>
    </ViewPlanPageCard>
  );
};
